// frontend/src/components/churches/AddEditServiceDefinitionModal.tsx

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import {
  createServiceDefinition,
  updateServiceDefinition,
  ServiceDefinition,
  toggleServiceDefinition
} from '../../services/churchService';
import { useError } from '../../contexts/ErrorContext';

interface AddEditServiceDefinitionModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  churchId: number;
  serviceDefinition: ServiceDefinition | null; // null for add, ServiceDefinition for edit
}

export const AddEditServiceDefinitionModal: React.FC<AddEditServiceDefinitionModalProps> = ({
  show,
  onHide,
  onSuccess,
  churchId,
  serviceDefinition
}) => {
  const [serviceName, setServiceName] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [hour, setHour] = useState(10);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setError } = useError();

  useEffect(() => {
    if (serviceDefinition) {
      setServiceName(serviceDefinition.service_name);
      setDayOfWeek(serviceDefinition.day_of_week);
      setIsActive(serviceDefinition.is_active);

      // Parse time
      const match = serviceDefinition.time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (match) {
        setHour(parseInt(match[1], 10));
        setMinute(parseInt(match[2], 10));
        setPeriod(match[3].toUpperCase() as 'AM' | 'PM');
      }
    } else {
      setServiceName('');
      setDayOfWeek(0);
      setHour(10);
      setMinute(0);
      setPeriod('AM');
      setIsActive(true);
    }
  }, [serviceDefinition, show]);

  const formatTime = (): string => {
    return `${hour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serviceName.trim()) {
      setError(new Error('Service name is required'));
      return;
    }

    if (hour < 1 || hour > 12) {
      setError(new Error('Hour must be between 1 and 12'));
      return;
    }

    try {
      setIsSubmitting(true);
      const time = formatTime();

      if (serviceDefinition) {
        // Edit existing service definition
        await updateServiceDefinition(serviceDefinition.service_def_id, {
          service_name: serviceName.trim(),
          day_of_week: dayOfWeek,
          time: time,
          is_active: isActive
        });
      } else {
        // Create new service definition
        await createServiceDefinition({
          church_id: churchId,
          service_name: serviceName.trim(),
          day_of_week: dayOfWeek,
          time: time,
          is_active: isActive
        });
      }

      onSuccess();
      onHide();
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Failed to save service definition'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onHide();
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {serviceDefinition ? 'Edit Service Definition' : 'Add Service Definition'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Service Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              placeholder="e.g., Sunday Mass, Morning Prayer"
              maxLength={100}
              required
              autoFocus
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Day of Week <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))}
                  required
                >
                  {dayNames.map((day, index) => (
                    <option key={index} value={index}>
                      {day}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Time <span className="text-danger">*</span>
                </Form.Label>
                <Row>
                  <Col xs={4}>
                    <Form.Select
                      value={hour}
                      onChange={(e) => setHour(parseInt(e.target.value, 10))}
                      required
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={4}>
                    <Form.Select
                      value={minute}
                      onChange={(e) => setMinute(parseInt(e.target.value, 10))}
                      required
                    >
                      {[0, 15, 30, 45].map(m => (
                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={4}>
                    <Form.Select
                      value={period}
                      onChange={(e) => setPeriod(e.target.value as 'AM' | 'PM')}
                      required
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Form.Text className="text-muted">
                  Preview: {formatTime()}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active (this service definition is currently in use)"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : serviceDefinition ? 'Save Changes' : 'Create Service Definition'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
